import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Bin from "../../../../images/red_bin.svg";
import { MODE_DELIVERY } from "../../../../constants/constants";
import EmptyCartIcon from "../../../../images/empty-cartIcon.svg";
import { Cart, RestaurantCart } from "../../../../interfaces/Catalogue";
import { updateCart } from "../../../../store/cart/cartActions";
import { getTotalOrder } from "../../../../utils/priceUtils";
import CartHeader from "../../../shared/AppNavbar/CartModal/CartHeader/CartHeader";
import OrderDetails from "../../../shared/OrderDetails/OrderDetails";
import "./CartMenu.scss";
import { shopApi } from "../../../../services/apiGate";
import { getIcon, IconType } from "../../../../theme/icons";
import { useConfig } from "../../../../context/ConfigContext";

interface CartItem {
  id: string;
  name: string;
  quantity: number;
  price: number;
}

interface CartMenuProps {
  cartItems?: CartItem[];
  onCartClick?: () => void;
}

const CartMenu: React.FC<CartMenuProps> = ({
  cartItems = [],
  onCartClick,
}) => {
  const { config } = useConfig();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [ShowRestaurantError, setShowRestaurantError] = useState(false);

  const selectedRestaurant = useSelector((state: any) => state.shop.selectedRestaurant);
  const savedCategories = useSelector((state: { cart: Cart }) => state.cart.savedCategories);

  const [restaurantCart, setRestaurantCart] = useState<RestaurantCart | null>(null);

  // Update cart when restaurant or saved categories change
  useEffect(() => {
    if (selectedRestaurant) {
      setRestaurantCart(savedCategories[selectedRestaurant.id] || null);
    } else {
      setRestaurantCart(null);
    }
  }, [savedCategories, selectedRestaurant]);

  const totalAmount = useMemo(
    () => (restaurantCart?.categories ? getTotalOrder(restaurantCart.categories) : 0),
    [restaurantCart]
  );

  const isCheckoutDisabled = useMemo(() => {
    if (!restaurantCart || restaurantCart.mode !== MODE_DELIVERY) return false;
    return totalAmount === 0 || totalAmount < (selectedRestaurant?.cartThreshold || 0);
  }, [restaurantCart, totalAmount, selectedRestaurant]);

  const totalItems = restaurantCart?.categories?.length || 0;

  const toggleCart = () => {
    setIsOpen(!isOpen);
    onCartClick?.();
  };

  const goToCart = () => {
    navigate("/cart");
    setIsOpen(false);
  };

  const checkRestaurantDisponibilty = async (): Promise<boolean> => {
    try {
      const response = await shopApi.getResturantById({
        id: Number(selectedRestaurant?.id),
        diningMode: Number(restaurantCart?.mode || 0),
        adresse: {
          latitude: 0,
          longitude: 0,
        },
      });
      const { restaurant } = response.data;
      return !!(restaurant?.isEnabled && restaurant?.isOpen);
    } catch {
      return false;
    }
  };

  const checkUserAuthentication = () => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      localStorage.setItem("redirectedToAuth", "true");
      navigate("/Authentification");
    } else {
      localStorage.setItem("redirectedToAuth", "false");
      navigate("/paiement");
    }
  };

  const goToCheckout = async () => {
    const disponible = await checkRestaurantDisponibilty();
    if (disponible) {
      localStorage.setItem("restaurantId", String(selectedRestaurant?.id));
      checkUserAuthentication();
    } else {
      setShowRestaurantError(true);
    }
    setIsOpen(false);
  };

  const handleCancel = () => {
    let updatedCategories = { ...savedCategories };
    delete updatedCategories[selectedRestaurant?.id || ""];
    dispatch(updateCart(updatedCategories));
  };

  const goToRestaurant = () => {
    if (selectedRestaurant) navigate(`/restaurant/${selectedRestaurant.id}`);
  };

  const getTotalPrice = useMemo(
    () => cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0),
    [cartItems]
  );

  return (
    <div className="cart-menu-container">
      {/* Cart Button */}
      <button className="auth-cart-btn" onClick={toggleCart}>
        {/* <span className="cart-icon">🛒


        </span> */}
        {getIcon(IconType.PANIER, {
          color: config?.primaryColor,
          onClick: () => toggleCart(),
        })}
        {totalItems > 0 && <span className="cart-badge">{totalItems}</span>}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          <div className="cart-dropdown">
            <div className="cart-dropdown-content">
              {/* Empty Cart */}
              {!restaurantCart?.categories?.length ? (
                <div className="panier-wrapper">
                  <img className="empty-cart-icon" src={EmptyCartIcon} alt="EmptyCart" />
                  <p className="empty-cart-title">Aucun Article ajouté</p>
                  <span className="empty-cart-subtitle">Ajoutez des articles, ils seront affichés ici.</span>
                </div>
              ) : (
                <>
                  {/* Cart Header */}
                  <div className="cart-components">
                    <CartHeader restaurantCart={restaurantCart} restaurant={selectedRestaurant} />
                  </div>

                  <div className="cart-dropdown-divider" />

                  <OrderDetails
                    categories={restaurantCart?.categories || []}
                    orderType={restaurantCart?.mode || 0}
                    canApplyArticleDiscount={false}
                    canEditQuantity={true}
                    restaurantId={selectedRestaurant?.id || ""}
                  />

                  <div className="cancel-btn-wrapper">
                    <button className="cancel-btn" onClick={handleCancel}>
                      <img src={Bin} alt="" />
                      Annuler commande
                    </button>
                  </div>

                  {/* Footer Buttons */}
                  <div className="cart-footer">
                    <button
                      className={`btn-command ${isCheckoutDisabled ? "btn-disabled" : ""}`}
                      disabled={isCheckoutDisabled}
                      onClick={() => {
                        localStorage.setItem("restaurantId", String(selectedRestaurant?.id));
                        goToCheckout();
                      }}
                    >
                      Commander • {totalAmount}€
                    </button>

                    <button onClick={goToRestaurant} className="btn-add-articles">
                      Ajouter article(s)
                    </button>
                  </div>
                </>
              )}

              {/* Cart Items (Optional external cartItems prop) */}
              {cartItems.length > 0 && (
                <>
                  <div className="cart-items">
                    {cartItems.map((item) => (
                      <div key={item.id} className="cart-item">
                        <div className="cart-item-details">
                          <div className="cart-item-name">{item.name}</div>
                          <div className="cart-item-quantity">Quantité: {item.quantity}</div>
                        </div>
                        <div className="cart-item-price">{item.price.toFixed(2)} €</div>
                      </div>
                    ))}
                  </div>

                  <div className="cart-dropdown-divider" />
                  <div className="cart-total">
                    <div className="total-label">Total:</div>
                    <div className="total-price">{getTotalPrice.toFixed(2)} €</div>
                  </div>

                  <div className="cart-actions">
                    <button className="cart-action-btn secondary" onClick={goToCart}>
                      Voir le panier
                    </button>
                    <button className="cart-action-btn primary" onClick={goToCheckout}>
                      Commander
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Overlay to close dropdown */}
          <div className="cart-dropdown-overlay" onClick={() => setIsOpen(false)} />
        </>
      )}
    </div>
  );
};

export default CartMenu;